import { motion } from "framer-motion";

/**
 * Horizontal era pathway with colour-coded markers, a sweeping fill animation,
 * and a "traveller" dot that moves across the timeline.
 */
const ERAS = [
  { n: "I", label: "Trade", colour: "#8B5E3C", note: "Pre-1800s" },
  { n: "II", label: "Paper", colour: "#A2845E", note: "1800s — 1950s" },
  { n: "III", label: "Computers", colour: "#146C73", note: "1960s — 1990s" },
  { n: "IV", label: "Web", colour: "#1E8A91", note: "1995 — 2010" },
  { n: "V", label: "Automation", colour: "#C8941F", note: "2010 — 2022" },
  { n: "VI", label: "AI", colour: "#E63329", note: "2023 →" },
];

export default function EraPathway({ active = 5, className = "" }) {
  return (
    <div className={`relative w-full ${className}`} aria-hidden>
      {/* Horizontal rail */}
      <div className="relative h-px bg-ink/15">
        {/* Animated coloured progress fill */}
        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
          style={{ transformOrigin: "left" }}
          className="absolute inset-0 h-px bg-gradient-to-r from-[#8B5E3C] via-[#146C73] via-[#C8941F] to-brand-red"
        />

        {/* Traveling dot — slides across the rail and lands on active */}
        <motion.span
          initial={{ left: "0%", opacity: 0 }}
          whileInView={{
            left: `${(active / (ERAS.length - 1)) * 100}%`,
            opacity: 1,
          }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 2.2, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2"
        >
          {/* Halo ring */}
          <span className="absolute inset-0 -m-2 rounded-full bg-brand-red/25 animate-ping" />
          {/* Solid head */}
          <span className="relative block w-4 h-4 rounded-full bg-brand-red shadow-[0_0_0_3px_rgba(255,255,255,1),0_0_0_4px_rgba(230,51,41,0.35)]" />
        </motion.span>
      </div>

      {/* Markers */}
      <div className="absolute inset-x-0 -top-1.5 grid grid-cols-6">
        {ERAS.map((e, i) => {
          const reached = i <= active;
          return (
            <div key={e.n} className="relative flex flex-col items-start">
              <motion.span
                initial={{ scale: 0, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: 0.25 + i * 0.18, duration: 0.5 }}
                style={{
                  backgroundColor: reached ? e.colour : "var(--bg-base)",
                  borderColor: reached ? e.colour : "rgba(15,17,21,0.4)",
                }}
                className="block w-3 h-3 rounded-full border"
              />
            </div>
          );
        })}
      </div>

      {/* Labels under */}
      <div className="grid grid-cols-6 mt-6">
        {ERAS.map((e, i) => (
          <motion.div
            key={e.n}
            initial={{ opacity: 0, y: 6 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ delay: 0.35 + i * 0.12, duration: 0.5 }}
            className="flex flex-col gap-1"
          >
            <span
              className="overline text-[10px]"
              style={{ color: e.colour }}
            >
              {e.n}
            </span>
            <span
              className="text-[12px] tracking-[0.04em] font-semibold"
              style={{ color: i === active ? e.colour : "rgba(15,17,21,0.7)" }}
            >
              {e.label}
            </span>
            <span className="text-[10px] tracking-[0.04em] text-ink-soft hidden md:inline">
              {e.note}
            </span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
